import React from "react";
import styled from "styled-components";
import { greyText, lightGreys } from "../../../styles";
import { CompassParagraph } from "../styledComponents";

const CardContainer = styled.div`
  background: white;
  border: 1px solid ${lightGreys.borderLightGrey};
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  flex: 1;
  min-width: 140px;
`;

export const ClassificationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
`;

const CardImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: contain;
`;

const RationaleBox = styled.div`
  background: ${lightGreys.KPIBackground};
  border-radius: 4px;
  padding: 8px;
  width: 100%;
  margin-top: 4px;
  text-align: left;
`;

const RationaleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  color: ${greyText};
  margin-bottom: 4px;
`;

const CategoryLabel = styled(CompassParagraph)`
  margin: 0;
  font-size: 9px;
  color: ${greyText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ValueLabel = styled(CompassParagraph)`
  margin: 0;
  font-size: 11px;
  color: ${greyText};
`;

const RationaleText = styled(CompassParagraph)`
  margin: 0;
  font-size: 10px;
  color: ${greyText};
`;

export interface ClassificationCardProps {
  /** Image source for the card icon */
  imageSrc: string;
  /** Alt text for the image */
  imageAlt?: string;
  /** Category/type label (displayed in uppercase) */
  category: string;
  /** The display value */
  value: string;
  /** Optional rationale text */
  rationale?: string;
  /** Optional custom rationale label (defaults to "Rationale") */
  rationaleLabel?: string;
}

/**
 * A card component for displaying categorized information with an icon,
 * category label, value, and optional rationale explanation.
 * Commonly used in grids (see ClassificationGrid) for displaying
 * classification frameworks, data categories, or similar grouped data.
 */
export const ClassificationCard: React.FC<ClassificationCardProps> = ({
  imageSrc,
  imageAlt = "",
  category,
  value,
  rationale,
  rationaleLabel = "Rationale",
}) => {
  return (
    <CardContainer>
      <CardImage src={imageSrc} alt={imageAlt} />
      <CategoryLabel>{category}</CategoryLabel>
      <ValueLabel>{value}</ValueLabel>
      {rationale && (
        <RationaleBox>
          <RationaleLabel>{rationaleLabel}</RationaleLabel>
          <RationaleText>{rationale}</RationaleText>
        </RationaleBox>
      )}
    </CardContainer>
  );
};
